import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Spinner, Button, Badge, Alert } from 'react-bootstrap';
import { useLanguage } from '../context/LanguageContext';
import { formatDistanceToNow } from 'date-fns';
// Using simple text symbols instead of react-icons due to version compatibility

interface Asset {
  asset?: string;
  asset_type?: string;
  asset_code?: string;
  asset_issuer?: string;
  amount?: string;
}

interface Operation {
  id: string;
  paging_token: string;
  transaction_successful: boolean;
  source_account: string;
  type: string;
  type_i: number;
  created_at: string;
  transaction_hash: string;
  
  // Liquidity Pool Deposit specific fields
  liquidity_pool_id?: string;
  reserves_max?: Asset[];
  reserves_deposited?: Asset[];
  shares_received?: string;
  min_price?: string;
  max_price?: string;
  min_price_r?: { n: number; d: number };
  max_price_r?: { n: number; d: number };
  
  // Path Payment specific fields
  asset_type?: string;
  asset_code?: string;
  asset_issuer?: string;
  from?: string;
  to?: string;
  amount?: string;
  path?: Asset[];
  source_amount?: string;
  destination_min?: string;
  source_asset_type?: string;
  source_asset_code?: string;
  source_asset_issuer?: string;
  
  // Change Trust specific fields
  limit?: string;
  trustor?: string;
  
  _links: {
    self: {
      href: string;
    };
    transaction: {
      href: string;
    };
    effects: {
      href: string;
    };
  };
}

interface ApiResponse {
  _embedded: {
    records: Operation[];
  };
  _links: {
    self: {
      href: string;
    };
    next?: {
      href: string;
    };
    prev?: {
      href: string;
    };
  };
}

const PoolHistory: React.FC = () => {
  const { poolId } = useParams<{ poolId: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [operations, setOperations] = useState<Operation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [nextLink, setNextLink] = useState<string | null>(null);
  const [prevLink, setPrevLink] = useState<string | null>(null);

  const fetchOperations = useCallback(async () => {
    if (!poolId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(
        `https://api.testnet.minepi.com/liquidity_pools/${poolId}/operations?limit=200&order=desc`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch operations');
      }
      
      const data: ApiResponse = await response.json();
      setOperations(data._embedded?.records || []);
      setNextLink(data._links?.next?.href || null);
      setPrevLink(data._links?.prev?.href || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [poolId]);

  useEffect(() => {
    fetchOperations();
  }, [fetchOperations]);

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatAsset = (asset?: Asset): string => {
    if (!asset) return '-';
    
    if (asset.asset) {
      // Format like "Archimedes:GBP7HXY4..." but shorten the issuer part
      const parts = asset.asset.split(':');
      if (parts.length === 2) {
        const [assetCode, issuer] = parts;
        return `${assetCode}:${issuer.substring(0, 8)}...`;
      }
      return asset.asset;
    }
    
    if (asset.asset_type === 'native') {
      return 'PI (native)';
    }
    
    if (asset.asset_code && asset.asset_issuer) {
      return `${asset.asset_code}:${asset.asset_issuer.substring(0, 8)}...`;
    }
    
    return asset.asset_type || 'Unknown';
  };

  const formatAmount = (amount?: string): string => {
    if (!amount) return '-';
    return parseFloat(amount).toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 7 
    });
  };

  const getOperationTypeLabel = (type: string, operation?: Operation): string => {
    // Check if it's a trade operation (from == to)
    if ((type === 'path_payment_strict_send' || type === 'path_payment_strict_receive') && 
        operation && operation.from === operation.to) {
      return 'Trade';
    }
    
    switch (type) {
      case 'liquidity_pool_deposit':
        return 'Pool Deposit';
      case 'liquidity_pool_withdraw':
        return 'Pool Withdraw';
      case 'change_trust':
        return 'Change Trust';
      case 'path_payment_strict_send':
        return 'Path Payment (Send)';
      case 'path_payment_strict_receive':
        return 'Path Payment (Receive)';
      case 'payment':
        return 'Payment';
      default:
        return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const getOperationTypeBadge = (type: string, operation?: Operation): string => {
    // Check if it's a trade operation (from == to)
    if ((type === 'path_payment_strict_send' || type === 'path_payment_strict_receive') && 
        operation && operation.from === operation.to) {
      return 'success';
    }
    
    switch (type) {
      case 'liquidity_pool_deposit':
        return 'success';
      case 'liquidity_pool_withdraw':
        return 'warning';
      case 'change_trust':
        return 'info';
      case 'path_payment_strict_send':
      case 'path_payment_strict_receive':
        return 'primary';
      case 'payment':
        return 'secondary';
      default:
        return 'dark';
    }
  };

  const renderOperationDetails = (operation: Operation): React.ReactElement => {
    switch (operation.type) {
      case 'liquidity_pool_deposit':
        return (
          <div className="small">
            <div className="mb-1">
              <strong>Deposited:</strong>
              {operation.reserves_deposited?.map((reserve, idx) => (
                <div key={idx} className="ms-2">
                  {formatAmount(reserve.amount)} {formatAsset(reserve)}
                </div>
              ))}
            </div>
            {operation.shares_received && (
              <div className="mb-1">
                <strong>Shares Received:</strong> {formatAmount(operation.shares_received)}
              </div>
            )}
            {operation.min_price && operation.max_price && (
              <div>
                <strong>Price Range:</strong> {formatAmount(operation.min_price)} - {formatAmount(operation.max_price)}
              </div>
            )}
          </div>
        );
      
      case 'path_payment_strict_send':
      case 'path_payment_strict_receive':
        const isTradeOperation = operation.from === operation.to;
        return (
          <div className="small">
            {isTradeOperation ? (
              <div className="mb-1">
                <strong>Account:</strong> {operation.from?.substring(0, 8)}...{operation.from?.substring(operation.from.length - 8)}
              </div>
            ) : (
              <>
                <div className="mb-1">
                  <strong>From:</strong> {operation.from?.substring(0, 8)}...{operation.from?.substring(operation.from.length - 8)}
                </div>
                <div className="mb-1">
                  <strong>To:</strong> {operation.to?.substring(0, 8)}...{operation.to?.substring(operation.to.length - 8)}
                </div>
              </>
            )}
            {isTradeOperation ? (
              <div className="mb-1">
                <strong>Swap:</strong> <strong>{formatAmount(operation.source_amount)}</strong> {operation.source_asset_code || 'PI'} {'=>'} <strong>{formatAmount(operation.amount)}</strong> {operation.asset_code || (operation.asset_type === 'native' ? 'PI' : '')}
              </div>
            ) : (
              <>
                <div className="mb-1">
                  <strong>Source Amount:</strong> {formatAmount(operation.source_amount)} 
                  {operation.source_asset_code ? ` ${operation.source_asset_code}` : ' PI'}
                </div>
                <div className="mb-1">
                  <strong>Destination Amount:</strong> {formatAmount(operation.amount)} 
                  {operation.asset_code ? ` ${operation.asset_code}` : (operation.asset_type === 'native' ? ' PI' : '')}
                </div>
              </>
            )}
            {operation.path && operation.path.length > 0 && (
              <div>
                <strong>Path:</strong>
                {operation.path.map((asset, idx) => (
                  <span key={idx} className="ms-1">
                    {idx > 0 && ' → '}
                    {asset.asset_code || 'PI'}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      
      case 'change_trust':
        return (
          <div className="small">
            <div className="mb-1">
              <strong>Trustor:</strong> {operation.trustor?.substring(0, 8)}...{operation.trustor?.substring(operation.trustor.length - 8)}
            </div>
            <div className="mb-1">
              <strong>Asset Type:</strong> {operation.asset_type?.replace(/_/g, ' ')}
            </div>
            {operation.limit && (
              <div>
                <strong>Limit:</strong> {formatAmount(operation.limit)}
              </div>
            )}
          </div>
        );
      
      default:
        return <span className="text-muted small">No additional details</span>;
    }
  };

  if (loading) {
    return (
      <Container className="my-5">
        <div className="d-flex justify-content-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <div className="alert alert-danger">{error}</div>
      </Container>
    );
  }

  return (
    <Container className="my-4">
      {/* Back Button */}
      <Row className="mb-3">
        <Col>
          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={() => navigate(-1)}
            className="d-flex align-items-center"
          >
            ← {t('poolHistory.back_to_pool')}
          </Button>
        </Col>
      </Row>

      {/* Page Header */}
      <Row className="mb-4">
        <Col>
          <h2 className="mb-1">{t('poolHistory.heading')}</h2>
          <p className="text-muted mb-0">
            {t('poolHistory.pool_id')}: <code>{poolId}</code>
          </p>
          <Alert variant="warning" className="mt-3 mb-0">
            <small>
              <strong>{t('poolHistory.testnet_warning')}</strong> {t('poolHistory.testnet_description')}
            </small>
          </Alert>
        </Col>
      </Row>



      {/* Operations Table */}
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{t('poolHistory.operation_history')}</h5>
              <div className="d-flex gap-2">
                {prevLink && (
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => {
                      fetch(prevLink)
                        .then(res => res.json())
                        .then(data => {
                          setOperations(data._embedded?.records || []);
                          setNextLink(data._links?.next?.href || null);
                          setPrevLink(data._links?.prev?.href || null);
                        })
                        .catch(console.error);
                    }}
                  >
                    ← {t('poolHistory.previous')}
                  </Button>
                )}
                {nextLink && (
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => {
                      fetch(nextLink)
                        .then(res => res.json())
                        .then(data => {
                          setOperations(data._embedded?.records || []);
                          setNextLink(data._links?.next?.href || null);
                          setPrevLink(data._links?.prev?.href || null);
                        })
                        .catch(console.error);
                    }}
                  >
                    {t('poolHistory.next')} →
                  </Button>
                )}
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table className="mb-0" hover>
                  <thead className="table-light">
                    <tr>
                      <th>{t('poolHistory.operation')}</th>
                      <th>{t('poolHistory.type')}</th>
                      <th>{t('poolHistory.details')}</th>
                      <th>{t('poolHistory.source_account')}</th>
                      <th>{t('poolHistory.time')}</th>
                      <th className="text-center">{t('poolHistory.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {operations.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-5">
                          <div>
                            <h6 className="text-muted">{t('poolHistory.no_operations')}</h6>
                            <small className="text-muted">
                              {t('poolHistory.no_operations_desc')}
                            </small>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      operations.map((operation, index) => (
                        <tr key={operation.id}>
                          <td>
                            <div className="d-flex flex-column">
                              <small className="text-muted">#{operation.id}</small>
                              <code className="small text-break">
                                {operation.transaction_hash.substring(0, 8)}...
                                {operation.transaction_hash.substring(operation.transaction_hash.length - 8)}
                              </code>
                            </div>
                          </td>
                          <td>
                            <Badge bg={getOperationTypeBadge(operation.type, operation)} className="me-1">
                              {getOperationTypeLabel(operation.type, operation)}
                            </Badge>
                          </td>
                          <td style={{ minWidth: '250px' }}>
                            {renderOperationDetails(operation)}
                          </td>
                          <td>
                            <code className="small">
                              {operation.source_account.substring(0, 8)}...
                              {operation.source_account.substring(operation.source_account.length - 8)}
                            </code>
                          </td>
                          <td>
                            <div className="d-flex flex-column">
                              <small>{formatDateTime(operation.created_at)}</small>
                              <small className="text-muted">
                                {formatDistanceToNow(new Date(operation.created_at), { addSuffix: true })}
                              </small>
                            </div>
                          </td>
                          <td className="text-center">
                            <Badge 
                              bg={operation.transaction_successful ? 'success' : 'danger'} 
                              className="small"
                            >
                              {operation.transaction_successful ? t('poolHistory.success') : t('poolHistory.failed')}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default PoolHistory;
